import { mediaDisplayName, withMediaDisplayName } from "../helpers/mediaDisplayName";
import { EventBus } from "../helpers/EventBus";
import { mediaTreeCache, emptyMediaTreeState, MediaTreeState } from "./MediaTreeCache";
import { IApiResponse, IMediaTreeItem, IMediaItem, IMediaFolder, ISiteFolder } from "../models/Interfaces";
import { BaseService } from "./BaseService";

export class MediaService extends BaseService {

  private mediaIdentity = () => {
    const token = this._tokenService.getTokenNoRedirect();
    return token?.email || token?.user_id || null;
  };
  private mediaScope = (): string => JSON.stringify([this.mediaIdentity(), this.siteKey]);
  private snapshot?: MediaTreeState & { scope: string };
  public getMediaTreeState = () => {
    const scope = this.mediaScope();
    const state = this.siteKey && this.mediaIdentity() ? mediaTreeCache.snapshot(scope) : emptyMediaTreeState;
    if (!this.snapshot || this.snapshot.scope !== scope || this.snapshot.items !== state.items
      || this.snapshot.refreshing !== state.refreshing || this.snapshot.error !== state.error || this.snapshot.updatedAt !== state.updatedAt)
      this.snapshot = { ...state, scope };
    return this.snapshot;
  };
  public subscribeMediaTree = (listener: () => void) => {
    let unsubscribe = mediaTreeCache.subscribe(this.mediaScope(), listener);
    const changeSite = () => {
      unsubscribe();
      unsubscribe = mediaTreeCache.subscribe(this.mediaScope(), listener);
      listener();
    };
    EventBus.getInstance().subscribe(EventBus.Event.OnSiteSelected, changeSite);
    window.addEventListener("storage", changeSite);
    window.addEventListener("cms-session-ended", changeSite);
    return () => {
      unsubscribe();
      EventBus.getInstance().unsubscribe(EventBus.Event.OnSiteSelected, changeSite);
      window.removeEventListener("storage", changeSite);
      window.removeEventListener("cms-session-ended", changeSite);
    };
  };
  private fetchMediaTree = async (site: string): Promise<IMediaTreeItem[]> => {
    const res = await this._ajaxService.Get<IApiResponse>(`/SiteMedia/GetUploadedMediaTree/${site}/false`, true, undefined, undefined, true);
    if (res?.StatusCode === 404) return [];
    if (res?.StatusCode === 200 && Array.isArray(res.ReturnData))
      return res.ReturnData.map((item: IMediaTreeItem) => ({ ...item, text: mediaDisplayName(item.metadataJson, item.text) }));
    throw new Error("Failed to retrieve site media");
  };
  public getMediaTree = (): Promise<IMediaTreeItem[]> => {
    const site = this.siteKey;
    return site && this.mediaIdentity() ? mediaTreeCache.load(this.mediaScope(), () => this.fetchMediaTree(site)) : Promise.resolve([]);
  };
  public refreshMediaTree = (): Promise<IMediaTreeItem[]> => {
    const site = this.siteKey;
    return site && this.mediaIdentity() ? mediaTreeCache.refresh(this.mediaScope(), () => this.fetchMediaTree(site)) : Promise.resolve([]);
  };

  public getSiteFolders = async (): Promise<Array<ISiteFolder>> => {
    if (this.siteKey) {
      const url: string = `/SiteMedia/GetMediaFolders/${this.siteKey}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200 && res.ReturnData) {
        return res.ReturnData;
      } else {
        if (res?.StatusCode !== 404) {
          this.EmitDataError(res, "Failed to retrieve site folders");
        }
      }
    }
    return [];
  }

  public getAllSiteMedia = async (): Promise<Array<IMediaItem>> => {
    if (this.siteKey) {
      const url: string = `/SiteMedia/GetAllMediaUploads/${this.siteKey}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200 && res.ReturnData) {
        return res.ReturnData.map(withMediaDisplayName);
      } else {
        if (res?.StatusCode !== 404) {
          this.EmitDataError(res, "Failed to retrieve site media/uploads");
        }
      }
    }
    return [];
  }

  public getFilesForFolder = async (folderid: number) : Promise<Array<IMediaItem>> => {
    if (this.siteKey) {
      const url: string = `/SiteMedia/GetMediaUploads/${this.siteKey}/${folderid}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200 && res.ReturnData) {
        return res.ReturnData.map(withMediaDisplayName);
      } else {
        if (res?.StatusCode !== 404) {
          this.EmitDataError(res, "Failed to retrieve media for selected folder");
        }
      }
    }
    return [];
  }

  public uploadFile = async (mediafile: File, folderId: number) : Promise<IMediaItem | null> => {
    if (this.siteKey && mediafile && folderId) {
      const itemarray = [mediafile];
      const res = await this.uploadFiles(itemarray, folderId);
      if (res && res.length > 0) {
        return res[0];
      }
    }
    return null;
  }

  public uploadFiles = async (mediafiles: Array<File>, folderid: number): Promise<Array<IMediaItem> | null> => {
    const mediaScope = this.mediaScope();
    if (this.siteKey && mediafiles?.length > 0) {
      const url: string = `/SiteMedia/FileUpload/${this.siteKey}/${folderid}`;
      const formData = new FormData();
  
      // Append each file to the form data
      mediafiles.forEach(file => {
        formData.append("files", file); // Ensure "files" matches the server's expected parameter name
      });
  
      // Assuming _ajaxService.Post is set up to handle FormData
      const res = await this._ajaxService.PostFormData<IApiResponse>(url, formData, true);
  
      if (res?.StatusCode === 200 && res?.ReturnData) {
        mediaTreeCache.invalidate(mediaScope);
        return res.ReturnData.map(withMediaDisplayName);
      } else {
        this.EmitDataError(res, "Failed to save media item");
      }
    }
    return null;
  }

  public downloadFileContent = async (fileId: number): Promise<Blob> => {
    if (!this.siteKey || !fileId) {
      throw new Error("The file could not be downloaded.");
    }

    return await this._ajaxService.GetBlob(`/SiteMedia/FileContent/${this.siteKey}/${fileId}`, true);
  }

  public replaceFile = async (mediafile: File, fileId: number): Promise<IMediaItem | null> => {
    const mediaScope = this.mediaScope();
    if (this.siteKey && mediafile && fileId) {
      const url: string = `/SiteMedia/ReplaceFile/${this.siteKey}/${fileId}`;
      const formData = new FormData();
      formData.append("files", mediafile);
      const res = await this._ajaxService.PostFormData<IApiResponse>(url, formData, true);

      if (res?.StatusCode === 200 && res?.ReturnData) {
        mediaTreeCache.invalidate(mediaScope);
        return withMediaDisplayName(res.ReturnData);
      } else {
        this.EmitDataError(res, "Failed to replace media item");
      }
    }
    return null;
  }

  public renameFile = async (fileId: number, filename: string): Promise<Pick<IMediaItem, "Id" | "Filename" | "MetadataJson">> => {
    const mediaScope = this.mediaScope();
    if (!this.siteKey) throw new Error("Select a site before renaming a file.");
    try {
      const res = await this._ajaxService.Post<IApiResponse>(`/SiteMedia/RenameFile/${this.siteKey}/${fileId}`, { Filename: filename }, true);
      if (res?.StatusCode === 200 && res.ReturnData) { mediaTreeCache.invalidate(mediaScope); return res.ReturnData; }
      throw new Error(res?.Messages?.[0] || "The file could not be renamed. Please retry.");
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error((error as IApiResponse | null)?.Messages?.[0] || "The file could not be renamed. Please retry.");
    }
  };

  public deleteMediaItem = async (itemid: number) : Promise<boolean> => {
    const mediaScope = this.mediaScope();
    if (this.siteKey && itemid) {
      const url: string = `/SiteMedia/DeleteFile/${this.siteKey}/${itemid}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200) {
        mediaTreeCache.invalidate(mediaScope);
        return true;
      } else {
        this.EmitDataError(res, "Failed to remove selected file");
      }
    }
    return false;
  }

  public deleteFolder = async (folderid: number) : Promise<boolean> => {
    const mediaScope = this.mediaScope();
    if (this.siteKey && folderid) {
      const url: string = `/SiteMedia/DeleteMediaFolder/${this.siteKey}/${folderid}/true/true`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200) {
        mediaTreeCache.invalidate(mediaScope);
        return true;
      } else {
        this.EmitDataError(res, "Failed to remove selected folder");
      }
    }
    return false;
  }

  public saveFolder = async (folder: ISiteFolder) : Promise<ISiteFolder | null> => {
    const mediaScope = this.mediaScope();
    if (this.siteKey && folder) {
      const url: string = `/SiteMedia/AddUpdateMediaFolder`;
      const model = {
        Id: folder.Id,
        ParentFolderId: folder.ParentId,
        FolderName: folder.FolderName,
        SiteId: this.siteKey
      };
      const res = await this._ajaxService.Post<IApiResponse>(url, model, true);

      if (res?.StatusCode === 200 && res.ReturnData) {
        mediaTreeCache.invalidate(mediaScope);
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Failed to save folder details");
      }
    }
    return null;
  }

  public moveMediatItem = async (mediaId: number, parentId: number) : Promise<IMediaItem | null> => {
    const mediaScope = this.mediaScope();
    if (this.siteKey && mediaId && parentId) {
      const url: string = `/SiteMedia/MoveMediaFile/${this.siteKey}/${mediaId}/${parentId}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200 && res.ReturnData) {
        mediaTreeCache.invalidate(mediaScope);
        return withMediaDisplayName(res.ReturnData);
      } else {
        this.EmitDataError(res, "Failed to move media item");
      }
    }
    return null;
  }

  public newSiteFolder = (parentId: number | null = null): ISiteFolder | null => {
    if (this.siteKey) {
      return {
        Id: 0,
        FolderName: "",
        ParentId: parentId,
        SiteId: this.siteKey,
        HasFiles: false,
        Files: [],
        HasSubFolders: false,        
      }
    }
    return null;
  }
}